/** Settings that control how available booking times are displayed to customers. */
export interface TimeSlotsConfiguration {
  /**
   * Time slots configuration ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the time slots configuration is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the time slots configuration.
   *
   * Ignored when creating a time slots configuration.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the time slots configuration was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the time slots configuration was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /** Time zone display settings. */
  displayTimeZone?: DisplayTimeZone;
  /** Staff selection settings that control how staff members are assigned to bookings. */
  staffSelection?: StaffSelection;
  /** Time interval settings that control how available time slots are divided. */
  splitInterval?: SplitInterval;
  /**
   * App that owns this configuration. Set by the Bookings platform when the time slots configuration is created via [Provision Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/bookings-platform-provisioner/provision-bookings-platform).
   * @format GUID
   */
  appId?: string;
  /**
   * Custom field data for the time slots configuration object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

export interface DisplayTimeZone {
  /**
   * Whether the customer can change the time zone when booking or viewing an existing booking.
   *
   * Default: `true`
   */
  customerCanChange?: boolean | null;
  /**
   * Time zone used to display available times, either based on the business's or customer's location.
   *
   * Default: `BUSINESS_TIME_ZONE`
   */
  basedOn?: BasedOnWithLiterals;
  /**
   * The business's time zone, in IANA time zone format (for example, `America/New_York`).
   * Derived from the site's business properties.
   * @maxLength 255
   * @readonly
   */
  timeZone?: string | null;
}

export enum BasedOn {
  /** Display time zone is based on the business's location. */
  BUSINESS_TIME_ZONE = 'BUSINESS_TIME_ZONE',
  /** Display time zone is based on the customer's location. */
  CUSTOMER_TIME_ZONE = 'CUSTOMER_TIME_ZONE',
}

/** @enumType */
export type BasedOnWithLiterals =
  | BasedOn
  | 'BUSINESS_TIME_ZONE'
  | 'CUSTOMER_TIME_ZONE';

export interface StaffSelection {
  /** Staff selection strategy. */
  strategy?: StrategyWithLiterals;
}

export enum Strategy {
  /** Customer must explicitly select a staff member. Booking fails if no staff is selected. */
  CUSTOMER_MUST_CHOOSE = 'CUSTOMER_MUST_CHOOSE',
  /** Customer can select a specific staff member or choose "any available". */
  CUSTOMER_MAY_CHOOSE = 'CUSTOMER_MAY_CHOOSE',
  /** Staff selection is hidden from the customer. The system auto-assigns a staff member. */
  CUSTOMER_CANNOT_CHOOSE = 'CUSTOMER_CANNOT_CHOOSE',
}

/** @enumType */
export type StrategyWithLiterals =
  | Strategy
  | 'CUSTOMER_MUST_CHOOSE'
  | 'CUSTOMER_MAY_CHOOSE'
  | 'CUSTOMER_CANNOT_CHOOSE';

/** Interval between available slot start times. For example, 15 minutes to display available slots at 3:00, 3:15, and 3:30 PM. */
export interface SplitInterval {
  /**
   * Whether the slot duration is used as the interval value.
   * - `true`: The interval is identical to the service's slot duration.
   * - `false`: The interval is a fixed number of minutes specified in `valueInMinutes`.
   *
   * Default: `false`
   */
  sameAsDuration?: boolean | null;
  /**
   * Number of minutes between available slot start times.
   * Ignored when `sameAsDuration: true`.
   *
   * Default: `30`
   */
  valueInMinutes?: number | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateTimeSlotsConfigurationRequest {
  /** Time slots configuration to create. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface CreateTimeSlotsConfigurationResponse {
  /** Created time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface GetTimeSlotsConfigurationRequest {
  /**
   * Time slots configuration ID.
   * @format GUID
   */
  timeSlotsConfigurationId: string;
}

export interface GetTimeSlotsConfigurationResponse {
  /** Retrieved time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface GetTimeSlotsConfigurationByAppIdRequest {
  /**
   * App ID.
   * @format GUID
   */
  appId: string;
}

export interface GetTimeSlotsConfigurationByAppIdResponse {
  /** Retrieved time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface UpdateTimeSlotsConfigurationRequest {
  /** Time slots configuration to update. */
  timeSlotsConfiguration: TimeSlotsConfiguration;
}

export interface UpdateTimeSlotsConfigurationResponse {
  /** Updated time slots configuration. */
  timeSlotsConfiguration?: TimeSlotsConfiguration;
}

export interface QueryTimeSlotsConfigurationsRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryTimeSlotsConfigurationsResponse {
  /** List of time slots configurations. */
  timeSlotsConfigurations?: TimeSlotsConfiguration[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
